import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { Icon, type IconName } from "./Icon";
import { initials } from "@/lib/format";
import type { Role } from "@/api/types/Role";
import { useTranslation } from "react-i18next";

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

// ───────────── Button ─────────────

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  icon?: IconName;
  loading?: boolean;
};

const variants: Record<ButtonVariant, string> = {
  primary: "bg-accent border-accent text-accent-fg font-semibold hover:bg-accent-hover hover:border-accent-hover",
  secondary: "bg-surface border-border-strong text-text hover:bg-surface-2",
  ghost: "bg-transparent border-transparent text-text hover:bg-surface-2",
  danger: "bg-surface border-fail-fg text-fail-fg font-semibold hover:bg-fail-bg",
};

const sizes = { sm: "h-[30px] px-2.5 text-sm", md: "h-9 px-3.5", lg: "h-11 px-4" };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon, loading, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-ctl)] border font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-55",
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
});

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cx("inline-block size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent", className)}
    />
  );
}

// ───────────── Form fields ─────────────

const control =
  "w-full min-h-9 rounded-[var(--radius-ctl)] border border-border-strong bg-surface px-2.5 py-1.5 text-text placeholder:text-muted disabled:opacity-60 aria-[invalid=true]:border-fail-fg";

/** Single-line controls get a fixed height: native selects and inputs size their content box differently,
 * so padding alone leaves an input, a select and a combobox side by side at different heights. */
const singleLine = `${control.replace("py-1.5", "py-0")} h-9`;

/** Base control classes, minus any size utility the caller overrides (`w-auto`, `h-8`, `py-0`…).
 * Plain class concatenation can't override: whichever rule comes later in the CSS wins. */
const OVERRIDABLE = ["w-", "h-", "min-h-", "px-", "py-"];
function controlClass(className?: string, defaults = control) {
  const own = (className ?? "").split(/\s+/).filter(Boolean);
  const base = defaults
    .split(" ")
    .filter((b) => !OVERRIDABLE.some((p) => b.startsWith(p) && own.some((o) => o.startsWith(p))));
  return cx(base.join(" "), className);
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} className={controlClass(className, singleLine)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return <textarea ref={ref} className={controlClass(cx("min-h-20 resize-y leading-normal", className))} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={controlClass(cx("select-chevron pr-8", className), singleLine)} {...rest}>
      {children}
    </select>
  );
});

/** Label + control + hint/error. `children` receives the generated id. */
export function Field({
  label,
  hint,
  error,
  optional,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  children: (props: { id: string; "aria-describedby"?: string; "aria-invalid"?: boolean }) => ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-text-2">
        {label}
        {optional && <span className="font-normal text-muted"> · {t("common.optional")}</span>}
      </label>
      {children({ id, "aria-describedby": hint || error ? hintId : undefined, "aria-invalid": error ? true : undefined })}
      {(error || hint) && (
        <span id={hintId} className={cx("text-xs", error ? "text-fail-fg" : "text-muted")}>
          {error || hint}
        </span>
      )}
    </div>
  );
}

export function Checkbox({ label, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
      <input type="checkbox" className="size-4 accent-[var(--accent)]" {...rest} />
      <span>{label}</span>
    </label>
  );
}

/** Segmented control (radio group). */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex flex-wrap gap-0.5 rounded-lg bg-surface-2 p-[3px]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cx(
            "rounded-md px-3 py-1.5 text-sm",
            value === o.value ? "bg-surface font-semibold text-text shadow-[var(--shadow)]" : "text-muted hover:text-text",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ───────────── Display ─────────────

type ChipTone = "pass" | "fail" | "blocked" | "skip" | "accent" | "neutral" | "outline";
const tones: Record<ChipTone, string> = {
  pass: "bg-pass-bg text-pass-fg",
  fail: "bg-fail-bg text-fail-fg",
  blocked: "bg-blocked-bg text-blocked-fg",
  skip: "bg-skip-bg text-skip-fg",
  accent: "bg-accent-soft text-accent",
  neutral: "bg-surface-2 text-text-2",
  outline: "border border-dashed border-untested text-muted",
};

export function Chip({ tone = "neutral", icon, children }: { tone?: ChipTone; icon?: IconName; children: ReactNode }) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold leading-5",
        tones[tone],
      )}
    >
      {icon && <Icon name={icon} size={13} />}
      {children}
    </span>
  );
}

export function RoleChip({ role }: { role: Role }) {
  const { t } = useTranslation();
  return <Chip tone={role === "manager" ? "accent" : "neutral"}>{t(`roles.${role}`)}</Chip>;
}

/** A person's initials, or a robot face for a bot (an integration account). */
export function Avatar({ name, size = 24, bot }: { name: string; size?: number; bot?: boolean }) {
  if (bot) {
    return (
      <span
        aria-hidden="true"
        style={{ width: size, height: size }}
        className="inline-flex shrink-0 items-center justify-center rounded-[30%] bg-surface-2 text-text-2 ring-1 ring-border"
      >
        <Icon name="bot" size={Math.round(size * 0.62)} />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-accent-soft font-bold text-accent"
    >
      {initials(name)}
    </span>
  );
}

/** The small "bot" label after a bot's name. */
export function BotTag({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      title={t("common.botHint")}
      className={cx(
        "inline-flex shrink-0 items-center rounded border border-border px-1 align-middle text-[10px] font-semibold uppercase leading-[14px] tracking-wide text-muted",
        className,
      )}
    >
      {t("common.bot")}
    </span>
  );
}

/** A display name, with the bot label when it's a bot. */
export function PersonName({ user, className }: { user: { display_name: string; is_bot?: boolean }; className?: string }) {
  return (
    <span className={cx("inline-flex min-w-0 items-center gap-1", className)}>
      <span className="truncate">{user.display_name}</span>
      {user.is_bot && <BotTag />}
    </span>
  );
}

export function EmptyState({
  icon = "folder",
  title,
  body,
  action,
}: {
  icon?: IconName;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <span className="inline-flex size-11 items-center justify-center rounded-xl bg-surface-2 text-muted">
        <Icon name={icon} size={20} />
      </span>
      <div className="flex max-w-md flex-col gap-1">
        <h3 className="m-0 text-base font-semibold">{title}</h3>
        {body && <p className="m-0 text-muted">{body}</p>}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({ title, actions, children }: { title: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0 flex-[1_1_320px]">
        <h1 className="m-0 text-2xl leading-tight font-semibold tracking-tight">{title}</h1>
        {children}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Notice({ tone = "neutral", children }: { tone?: "neutral" | "blocked"; children: ReactNode }) {
  return (
    <div
      role="status"
      className={cx(
        "flex items-start gap-2.5 rounded-[var(--radius-card)] px-3.5 py-2.5 text-sm",
        tone === "blocked" ? "bg-blocked-bg text-blocked-fg" : "bg-surface-2 text-text-2",
      )}
    >
      <Icon name="alert" className="mt-0.5" />
      <span>{children}</span>
    </div>
  );
}

export function LoadError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon="alert"
      title={t("common.loadFailed")}
      action={
        <Button size="sm" onClick={onRetry}>
          {t("common.retry")}
        </Button>
      }
    />
  );
}

export function PageSpinner() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-40 items-center justify-center gap-2 text-muted" role="status">
      <Spinner /> {t("common.loading")}
    </div>
  );
}
